import { useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert02Icon, ArrowDown01Icon, ArrowRight01Icon, Clock01Icon } from '@hugeicons/core-free-icons';
import {
  EXTRA_SEAT_MONTHLY_PRICE_USD, getTrialChipCopy, getTrialDaysLeft, getTrialDeadlineCopy, getTrialHoursLeft,
  getTrialState, TRIAL_PAUSE_ITEMS, type TeamBillingInfo,
} from '@overlayqa/shared';
import { Button, buttonVariants } from '../ui/button';
import { cn } from '../../lib/utils';
import { ExtraSeatsControl } from './ExtraSeatsControl';
import { BillingInviteButton } from './BillingInviteButton';
import { ManageStripeSubscription } from './StripeBilling';
import './BillingPlanCard.css';

const runwayColors = {
  info: 'bg-[var(--color-trial-chip-info-bg)] text-[var(--color-trial-chip-info-text)]',
  warning: 'bg-[var(--color-trial-chip-warning-bg)] text-[var(--color-trial-chip-warning-text)]',
  urgent: 'bg-[var(--color-trial-chip-urgent-bg)] text-[var(--color-trial-chip-urgent-text)]',
};

// Same sizing hook ui/button.tsx gives its own `icon` prop, so these match every other button glyph.
const ctaIconClass = 'shrink-0 h-[var(--btn-icon-size)] w-[var(--btn-icon-size)]';

/**
 * Once the trial has ended, name the work they would lose (endowment effect,
 * builtformars.com/ux-glossary/endowment-effect). `issuesCaptured` already
 * excludes demo issues (billing.service.ts countTeamCapturedIssues); with
 * nothing captured there is nothing to name, so the general copy stays.
 */
function savedWorkCopy(expired: boolean, issuesCaptured: number | undefined): { heading: string; body: string } {
  if (expired && issuesCaptured && issuesCaptured > 0) {
    return {
      heading: `Your ${issuesCaptured} ${issuesCaptured === 1 ? 'issue is' : 'issues are'} saved`,
      body: 'They stay in your workspace and stay viewable. Pick a plan to keep capturing.',
    };
  }
  return {
    heading: expired ? 'Your work is saved' : 'When your trial ends',
    body: "Everything you've saved stays in your workspace and stays viewable.",
  };
}

/** Focus lands here when the subscription success message closes. */
export const PLAN_NAME_ID = 'current-plan-name';

/** Approved billing-card proposal, with the shared dashboard countdown and real actions. */
export function BillingPlanCard({ billingInfo, onUpgrade, justActivated = false }: {
  billingInfo: TeamBillingInfo;
  onUpgrade: () => void;
  /** A plan was confirmed during this visit: inviting is the next step while seats are free. */
  justActivated?: boolean;
}) {
  const [seatsOpen, setSeatsOpen] = useState(false);
  const [savingSeats, setSavingSeats] = useState(false);
  const seatTrigger = useRef<HTMLButtonElement>(null);
  const expired = billingInfo.plan === null;
  const isTrialing = !expired && getTrialHoursLeft(billingInfo.betaExpiresAt) > 0;
  // Members need the same timing facts. This is an informational badge, not
  // the owner's upgrade nudge; actions below still require isOwner.
  const state = getTrialState({ ...billingInfo, isOwner: true });
  const runway = isTrialing ? getTrialChipCopy(state, getTrialHoursLeft(billingInfo.betaExpiresAt),
    billingInfo.issuesCaptured, getTrialDaysLeft(billingInfo.betaExpiresAt, billingInfo.trialTimeZone)) : null;
  const deadline = isTrialing && billingInfo.betaExpiresAt && billingInfo.trialTimeZone
    ? getTrialDeadlineCopy(billingInfo.betaExpiresAt, billingInfo.trialTimeZone).replace('. Ends ', '.\nEnds ')
    : null;
  const canUpgrade = billingInfo.isOwner && (isTrialing || expired);
  const canEditSeats = billingInfo.isOwner && !expired && !isTrialing && Boolean(billingInfo.extraSeatsAvailable);
  const saved = savedWorkCopy(expired, billingInfo.issuesCaptured);
  const freeSeats = Number.isFinite(billingInfo.maxSeats) ? billingInfo.maxSeats - billingInfo.currentSeats : 0;
  const inviteNext = justActivated && billingInfo.isOwner && !expired && freeSeats > 0;
  const inviteVariant = inviteNext ? 'primary' : 'outline';

  return (
    <div className="card-sm mb-6 billing-plan-card" aria-labelledby="current-plan-heading">
      <div className="billing-plan-card__header">
        <div>
          <h2 id="current-plan-heading" className="section-label mb-3">Current Plan</h2>
          <div className="flex flex-wrap items-end gap-2" data-plan-heading>
            <h3 id={PLAN_NAME_ID} tabIndex={-1} className="hero-title focus:outline-none">{billingInfo.planName}</h3>
            {expired ? (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900">Trial ended</span>
            ) : isTrialing ? (
              <span className="inline-flex items-center rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-white ring-1 ring-inset ring-primary-600/20">Trial</span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">Active</span>
            )}
          </div>
          {billingInfo.isOwner && !expired && !isTrialing && billingInfo.planSource === 'stripe' && <ManageStripeSubscription inline disabled={savingSeats} />}
          {canUpgrade && (
            <div className="mt-4">
              <a href="#billing-plans" className={buttonVariants({ variant: 'primary', size: 'sm' })} onClick={onUpgrade}>
                <span className="text-cap-trim">Upgrade now</span>
                <HugeiconsIcon icon={ArrowRight01Icon} size={16} strokeWidth={2} color="currentColor" className={ctaIconClass} aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
        {runway && (
          <div className="billing-plan-card__timing">
            <span data-trial-runway title={runway.title} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold ${runwayColors[runway.variant]}`}>
              <HugeiconsIcon icon={runway.variant === 'info' ? Clock01Icon : Alert02Icon} size={14} strokeWidth={2} color="currentColor" className="shrink-0" aria-hidden="true" />
              {runway.label}
            </span>
            {deadline && <p data-trial-deadline className="mt-1 whitespace-pre-line text-sm font-normal text-gray-700">{deadline}</p>}
          </div>
        )}
      </div>

      {(isTrialing || expired) && (
        <div className="billing-plan-card__body border-t border-gray-100">
          <div className="billing-plan-card__reassurance">
            <h4 className="text-base font-semibold text-gray-900">{saved.heading}</h4>
            <p className="mt-2 text-sm font-normal text-gray-700">{saved.body}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900">{expired ? 'Paused until you pick a plan' : 'What pauses'}</h4>
            <ul className="billing-plan-card__list text-sm font-normal text-gray-700">
              {TRIAL_PAUSE_ITEMS.map(item => <li key={item}>{item}</li>)}
            </ul>
            {canUpgrade && (
              <a href="#billing-plans" className={cn(buttonVariants({ variant: 'link', size: 'sm' }), 'billing-plan-card__compare mt-3 px-0')} onClick={onUpgrade}>
                <span className="text-cap-trim">Compare plans</span>
                <HugeiconsIcon icon={ArrowDown01Icon} size={16} strokeWidth={2} color="currentColor" className={ctaIconClass} aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
      )}

      {canEditSeats ? <>
        <div className="billing-seat-manager">
          <div className="billing-seat-manager__header">
            <div><p className="text-sm text-gray-500">Seats</p><p className="mt-1 text-sm font-medium text-gray-900">{billingInfo.currentSeats} / {billingInfo.maxSeats} used</p></div>
            <Button ref={seatTrigger} type="button" variant="outline" size="sm" aria-expanded={seatsOpen} aria-controls="billing-extra-seats" disabled={savingSeats}
              onClick={() => { setSeatsOpen(!seatsOpen); if (seatsOpen) seatTrigger.current?.focus(); }}>
              {seatsOpen ? 'Close seat editor' : 'Add seats'}
            </Button>
          </div>
          {!seatsOpen && <p className="mt-2 text-sm text-gray-500">Extra seats: ${EXTRA_SEAT_MONTHLY_PRICE_USD}/seat/month</p>}
          {seatsOpen && <ExtraSeatsControl billingInfo={billingInfo} autoFocus onSavingChange={setSavingSeats} />}
        </div>
        <div className="billing-account-footer">
          <div><p className="text-sm text-gray-500">Role</p><p data-role-value className="mt-1 text-sm font-medium text-gray-900">Team Owner</p></div>
          <BillingInviteButton disabled={savingSeats} variant={inviteVariant} />
        </div>
      </> : <>
      <div className="billing-plan-card__footer border-t border-gray-100">
        {!expired && (
          <div className="billing-plan-card__seats">
            <p className="text-sm text-gray-500">Seats</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{billingInfo.currentSeats} / {billingInfo.maxSeats === Infinity ? '\u221E' : billingInfo.maxSeats} used</p>
          </div>
        )}
        <div className={expired ? 'billing-plan-card__role--solo' : 'billing-plan-card__role border-l border-gray-100'}>
          <p className="text-sm text-gray-500">Role</p>
          <p data-role-value className="mt-1 text-sm font-medium text-gray-900">{billingInfo.isOwner ? 'Team Owner' : 'Team Member'}</p>
        </div>
        {billingInfo.isOwner && !expired && <BillingInviteButton variant={inviteVariant} />}
      </div>
      </>}
      {inviteNext && <p className="mt-3 text-sm text-gray-600">{freeSeats} {freeSeats === 1 ? 'seat is' : 'seats are'} ready for your teammates.</p>}
      {!billingInfo.isOwner && <p className="mt-4 text-sm font-normal text-gray-700">Contact your team owner to manage the subscription.</p>}
    </div>
  );
}
